import * as React from 'react';
import {connect} from 'react-redux';

import {Field, formValueSelector, reset, change} from 'redux-form';

import {nonNull} from 'shells/form/validation/non-null';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconNotificationsIcon} from 'shells/icon/svg/sidebar-icon-notifications';
// @ts-expect-error - imported from .js/.jsx file
import {SlackIcon} from 'shells/icon/svg/slack-icon';
import {ReduxFormSelectTwoLineIcon} from 'shells/redux-form-fields/select-two-line-icon/select-two-line-icon';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';

import type {NextStepsFragment} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import {NextStepsFormSlackMessage, NextStepsFormNutshellNotification} from './forms';
import {getApiNextStepType, isNextStepUnconfigured} from './helpers';
import {FORM_NAME} from './next-steps-constants';
import type {NextStepType} from './types';

import styles from './next-steps-form.module.css';

const typeSelector = formValueSelector(FORM_NAME);

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        type: typeSelector(state, 'type'),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onTypeChange: (newValue?: string | null) => {
            dispatch(reset(FORM_NAME));
            dispatch(change(FORM_NAME, 'type', newValue));
        },
        onNotifiablesChange: (newValue?: string[] | null) => {
            dispatch(change(FORM_NAME, 'notifiables', newValue));
        },
    };
}

type OwnProps = {
    nextSteps: NextStepsFragment;
    startFromType: NextStepType | null | undefined;
    description: string;
    bodyText: string;
    slackMessagePreviewMessage: React.ReactNode;
    slackMessagePreviewTopRowText?: string;
    onlyAllowNotifyingSelf?: boolean;
};

type StateProps = {
    type?: NextStepType;
};

type DispatchProps = {
    onTypeChange: (newValue?: NextStepType | 'nutshellNotificationSelf' | null | undefined) => void;
    onNotifiablesChange: (arg1?: string[] | null | undefined) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

export function NextStepsFormComponent(props: Props) {
    const {type, onTypeChange, onNotifiablesChange} = props;
    const activeType = type || props.startFromType;
    const {user} = useGetSessionUser();

    React.useEffect(() => {
        onTypeChange(type);
    }, [type, onTypeChange, user, onNotifiablesChange]);

    React.useEffect(() => {
        // @ts-expect-error
        if (type === 'nutshellNotificationSelf' && user) {
            onNotifiablesChange([user.id]);
        }
    }, [type, user, onNotifiablesChange]);

    const nextStepValues = [
        {
            value: 'nutshellNotification',
            label: 'Send a notification',
            sublabel: 'Notify your team via Nutshell',
            icon: SidebarIconNotificationsIcon,
        },
        {
            value: 'slackMessage',
            label: 'Send a Slack notification',
            sublabel: 'Notify a certain channel on form submissions',
            icon: SlackIcon,
        },
    ];

    if (props.onlyAllowNotifyingSelf) {
        const notificationOption = {
            value: 'nutshellNotificationSelf',
            label: 'Send me a notification',
            sublabel: 'Notify me via Nutshell',
            icon: SidebarIconNotificationsIcon,
        } as const;

        nextStepValues.splice(0, 1, notificationOption);
    }

    return (
        <div className={styles['next-steps-form']}>
            <div className={styles['form-description']}>{props.description}</div>
            <SpacerGroup mb={8}>
                <Body>{props.bodyText}</Body>
            </SpacerGroup>
            {props.startFromType ? (
                <SpacerGroup mb={24}>
                    <NextStepsReadOnlyHeading
                        type={props.startFromType}
                        onlyAllowNotifyingSelf={props.onlyAllowNotifyingSelf}
                    />
                </SpacerGroup>
            ) : (
                <Field
                    name='type'
                    component={ReduxFormSelectTwoLineIcon}
                    options={nextStepValues.filter((selectValues) => {
                        // This is filtering out any next step types that
                        // are already configured.
                        // @ts-expect-error
                        const apiType = getApiNextStepType(selectValues.value);

                        return isNextStepUnconfigured(apiType, props.nextSteps);
                    })}
                    validate={[nonNull]}
                    placeholder='Select a notification type'
                />
            )}
            {activeType === 'slackMessage' ? (
                <NextStepsFormSlackMessage
                    previewMessage={props.slackMessagePreviewMessage}
                    topRowText={props.slackMessagePreviewTopRowText}
                />
            ) : undefined}
            {activeType === 'nutshellNotification' && !props.onlyAllowNotifyingSelf ? (
                <NextStepsFormNutshellNotification />
            ) : undefined}
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const NextStepsForm = connector(NextStepsFormComponent);

function NextStepsReadOnlyHeading(props: {type: NextStepType; onlyAllowNotifyingSelf?: boolean}) {
    const getContent = () => {
        if (props.type === 'slackMessage') {
            return 'Send a Slack message';
        } else if (props.onlyAllowNotifyingSelf) {
            return 'Send me a notification';
        } else return 'Send a notification';
    };

    return (
        <Body>
            <strong>{getContent()}</strong>
        </Body>
    );
}
